/*
 * This component is a card component for continuity across the app.
 *
 * @author Cargomatic Engineering
 * @usage <CargomaticCollapsibleCard />
 *
 */
import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getClassName } from '../../utils/ui/classes';
import { Card, CardContent, CardFooter, CardHeader } from '@cargomatic/ui';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { ChevronDown, ChevronRight } from 'lucide-react';

const CargomaticCollapsibleCard = forwardRef(
  (
    {
      id,
      children,
      grayFooter,
      header,
      headerParams,
      footer,
      className,
      dataTestId,
    },
    ref,
  ) => {
    const key = id ? `${id}-card-state` : undefined;

    const [cardState, setCardState] = useLocalStorage(key);
    const [open, setOpen] = useState(cardState?.open ?? true);

    const footerClassName = getClassName({
      'bg-gray-50': grayFooter,
    });

    const handleOpenClick = () => {
      setOpen(!open);
      setCardState({ open: !open });
    };

    return (
      <Card
        data-testid={dataTestId}
        ref={ref}
        className={getClassName(className)}
      >
        {header && (
          <CardHeader
            className="cursor-pointer select-none"
            onClick={(e) => {
              if (
                e.target.closest('button:not([data-testid="toggle-card"])') ||
                e.target.closest('a')
              )
                return;
              handleOpenClick();
            }}
          >
            <div className="flex items-center gap-2">
              <span data-testid="toggle-card" className="shrink-0">
                {open ? (
                  <ChevronDown className="w-5 h-5" />
                ) : (
                  <ChevronRight className="w-5 h-5" />
                )}
              </span>
              <div
                className="flex-1 text-base font-semibold leading-none tracking-tight"
                data-testid="div-5d044744"
              >
                {header(headerParams)}
              </div>
            </div>
          </CardHeader>
        )}
        {open && children && (
          <CardContent>
            <div data-testid="div-1fc043d2">{children}</div>
          </CardContent>
        )}
        {footer && (
          <CardFooter>
            <div data-testid="div-0e71b504" className={footerClassName}>
              {footer()}
            </div>
          </CardFooter>
        )}
      </Card>
    );
  },
);

CargomaticCollapsibleCard.displayName = 'CargomaticCollapsibleCard';

CargomaticCollapsibleCard.propTypes = {
  children: PropTypes.any,
  header: PropTypes.func,
  headerParams: PropTypes.object,
  footer: PropTypes.func,
  grayBody: PropTypes.bool,
  grayFooter: PropTypes.bool,
  removeVerticalPadding: PropTypes.bool,
  removeHorizontalPadding: PropTypes.bool,
  removeBorders: PropTypes.bool,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
};

CargomaticCollapsibleCard.defaultProps = {
  grayBody: false,
  grayFooter: false,
  removeHorizontalPadding: false,
  removeVerticalPadding: false,
  removeBorders: false,
};

export default CargomaticCollapsibleCard;
